import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconChartDots3, IconPlus, IconSettings, IconTrash } from "@tabler/icons-react";

import { api } from "../api/client";
import { ErrorState, LoadingState } from "../components/ui";
import { monoStyle } from "../theme";
import {
  generateDashboardId,
  useGrafanaDashboards,
  type GrafanaDashboard,
} from "../lib/grafanaDashboards";
import { useAutoSyncOnNav } from "../lib/autoSyncPref";

export const SAVED_SIMS_LIMIT_KEY = "settings.savedSimsLimit";
export const SAVED_SIMS_LIMIT_DEFAULT = 20;

export function getSavedSimsLimit(): number {
  try {
    const raw = localStorage.getItem(SAVED_SIMS_LIMIT_KEY);
    if (raw == null) return SAVED_SIMS_LIMIT_DEFAULT;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? Math.min(Math.max(1, n), 100) : SAVED_SIMS_LIMIT_DEFAULT;
  } catch {
    return SAVED_SIMS_LIMIT_DEFAULT;
  }
}

export function SettingsPage() {
  const [savedSimsLimit, setSavedSimsLimit] = useState<number | string>(getSavedSimsLimit);
  const [dashboards, setDashboards] = useGrafanaDashboards();
  const [autoSyncOnNav, setAutoSyncOnNav] = useAutoSyncOnNav();

  const updateDashboard = (id: string, patch: Partial<GrafanaDashboard>) => {
    setDashboards((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };
  const removeDashboard = (id: string) => {
    setDashboards((prev) => prev.filter((d) => d.id !== id));
  };
  const addDashboard = () => {
    setDashboards((prev) => [
      ...prev,
      { id: generateDashboardId(), title: "", url: "" },
    ]);
  };

  const healthQ = useQuery({
    queryKey: ["health"],
    queryFn: () => api.health(),
    staleTime: 15_000,
  });

  if (healthQ.isLoading) return <LoadingState />;
  if (healthQ.isError) return <ErrorState error={healthQ.error as Error} onRetry={() => healthQ.refetch()} />;

  const health = healthQ.data;
  if (!health) return <LoadingState label="Loading settings..." />;

  return (
    <Stack gap="md">
      <Group gap="xs" align="center">
        <IconSettings size={18} />
        <Title order={3}>Settings</Title>
      </Group>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={600}>Environment</Text>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Frontend shell</Text>
            <Badge color="cyan" variant="light">Mantine</Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">API version</Text>
            <Text size="sm" style={monoStyle}>{health.version}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">API status</Text>
            <Badge color={health.status === "ok" ? "green" : health.status === "degraded" ? "yellow" : "red"}>
              {health.status}
            </Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">DB health</Text>
            <Badge color={health.db === "ok" ? "green" : health.db === "degraded" ? "yellow" : "red"}>
              {health.db}
            </Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">WS relay</Text>
            <Badge color={health.ws_relay === "ok" ? "green" : health.ws_relay === "degraded" ? "yellow" : "red"}>
              {health.ws_relay}
            </Badge>
          </Group>
          <Divider />
          <Switch
            label="Auto-sync analytics data on navigation"
            description="When on, opening Market Data, Analytics, the Simulators or Grafana automatically pulls the latest live data into the analytics DB (throttled to once every 15s). When off, use the header 'Sync Data' button to refresh manually. Syncs required for correctness — before a simulation runs and after editing an outcome/order — always happen regardless of this setting."
            checked={autoSyncOnNav}
            onChange={(e) => setAutoSyncOnNav(e.currentTarget.checked)}
          />
          <Divider />
          <Text size="xs" c="dimmed">
            Other runtime toggles are managed server-side via env vars; this page surfaces status and reserved UI controls.
          </Text>
          <Switch label="Desktop notifications" disabled description="Coming soon" />
          <Switch label="Compact tables" disabled description="Coming soon" />
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={600}>Simulations</Text>
          <NumberInput
            label="Saved simulations list limit"
            description="Max number of runs shown on the Saved Simulations page (1–100)."
            value={savedSimsLimit}
            onChange={(val) => {
              setSavedSimsLimit(val);
              const n = typeof val === "number" ? val : parseInt(String(val), 10);
              if (Number.isFinite(n) && n >= 1 && n <= 100) {
                try { localStorage.setItem(SAVED_SIMS_LIMIT_KEY, String(n)); } catch { /* noop */ }
              }
            }}
            min={1}
            max={100}
            step={5}
            w={200}
          />
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Group gap="xs" align="center">
            <IconChartDots3 size={18} />
            <Text fw={600}>Grafana Dashboards</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Each entry appears as a sub-menu item under the Grafana section in the sidebar and is
            loaded inline with <code>&amp;kiosk</code> appended automatically.
          </Text>

          {dashboards.length === 0 ? (
            <Text size="sm" c="dimmed">No dashboards configured yet.</Text>
          ) : (
            <Stack gap="sm">
              {dashboards.map((d) => (
                <Group key={d.id} gap="sm" align="flex-end" wrap="nowrap">
                  <TextInput
                    label="Page title"
                    placeholder="Orders / Odds Movement"
                    value={d.title}
                    onChange={(e) => updateDashboard(d.id, { title: e.currentTarget.value })}
                    style={{ flex: "0 0 220px" }}
                  />
                  <TextInput
                    label="Dashboard URL"
                    placeholder="http://localhost:3000/d/<uid>/<slug>?orgId=1"
                    value={d.url}
                    onChange={(e) => updateDashboard(d.id, { url: e.currentTarget.value })}
                    style={{ flex: 1 }}
                  />
                  <Tooltip label="Remove dashboard">
                    <ActionIcon
                      color="red"
                      variant="light"
                      size="lg"
                      onClick={() => removeDashboard(d.id)}
                      aria-label={`Remove ${d.title || "dashboard"}`}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ))}
            </Stack>
          )}

          <Group>
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={addDashboard}
            >
              Add dashboard
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
